// dependencies
import React, { useRef } from "react";
import { z } from "zod";

// components
import {
	Checkbox,
	FormCheckboxGroup,
	FormDatePicker,
	FormField,
	FormMultipleSelect,
	PanelFilters,
	Text,
} from "@performyard/py-components";

import { MultipleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";
import { SingleSelectGoalAlignment } from "../../../../components/Inputs/SelectGoalAlignment";

// hooks
import { useCompanySettings } from "../../../../api/hooks";
import { useOutlooks } from "../../../../api/hooks/defs/outlook";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

// services
import { IStrategyCategory } from "../../../../api/services";

// utils
import { createFetchCategoryOptions, createFetchOutlookOptions } from "./filterDataFetchers";
import { migrateFiltersToNewFormat } from "./migrateFilters";

const statusOptions = [
	{ label: "Active", value: "active" },
	{ label: "Completed", value: "done" },
	{ label: "Archived", value: "archived" },
];

const transparencyOptions = [
	{ label: "Public", value: "public" },
	{ label: "Private", value: "private" },
];

const EmployeeGoalsFilterSchema = z.object({
	assignee: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
	createdDateAfter: z.date().nullable().optional(),
	createdDateBefore: z.date().nullable().optional(),
	dueDateAfter: z.date().nullable().optional(),
	dueDateBefore: z.date().nullable().optional(),
	category: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
	status: z.array(z.string()).default([]),
	transparency: z.array(z.string()).default([]),
	outlook: z.array(z.object({ id: z.string(), name: z.string(), color: z.string() })).default([]),
	showSharedOnly: z.array(z.string()).default([]),
	unseenUpdatesOnly: z.array(z.string()).default([]),
	alignedTo: z
		.object({
			id: z.string(),
			name: z.string(),
			status: z.string(),
			color: z.string(),
			assignee: z.string(),
			created: z.string(),
			due: z.string(),
			target: z.number(),
			goalValue: z.number(),
			progress: z.number(),
			categories: z.array(z.string()),
		})
		.nullable()
		.optional(),
});

export type EmployeeGoalsFilter = z.infer<typeof EmployeeGoalsFilterSchema>;

interface EmployeeGoalsPanelProps {
	isOpen: boolean;
	onClose: () => void;
	filters: Partial<EmployeeGoalsFilter>;
	onApplyFilters: (filters: Partial<EmployeeGoalsFilter>) => void;
}

export const EmployeeGoalsPanel = ({ isOpen, onClose, filters, onApplyFilters }: EmployeeGoalsPanelProps) => {
	const { data: companySettings } = useCompanySettings();
	const { value: ffGoalsDashboard } = useFeatureFlagQuery("ff-is-goal-overview-and-alignment-enabled", false);
	const goalDashboardFeatureEnabled =
		companySettings?.functions?.goalDashboard?.state === "enabled" && ffGoalsDashboard;

	const { data: outlooksData } = useOutlooks();

	const categoriesCache = useRef<IStrategyCategory[]>([]);

	const handleSubmit = ({
		value,
	}: {
		value: Partial<EmployeeGoalsFilter>;
		resetForm: () => void;
		setError: (field: keyof EmployeeGoalsFilter, error: string) => void;
	}) => {
		const formattedState = { ...value };

		// Remove empty, null, or undefined fields
		Object.keys(formattedState).forEach((key) => {
			const filterValue = formattedState[key];
			if (
				filterValue === null ||
				filterValue === undefined ||
				(Array.isArray(filterValue) && filterValue.length === 0)
			) {
				delete formattedState[key];
			}
		});

		onApplyFilters(formattedState);
		onClose();
	};

	/**
	 * Filter Migration:
	 * Migrate old filter format to new format before setting default values in the filter panel.
	 * This ensures the panel displays correctly even if old format filters are passed in.
	 * See migrateFilters.ts for details on why this migration is needed.
	 */
	const migratedFilters = migrateFiltersToNewFormat(filters || {});

	const defaultValues: EmployeeGoalsFilter = {
		assignee: migratedFilters.assignee || [],
		createdDateAfter: migratedFilters.createdDateAfter,
		createdDateBefore: migratedFilters.createdDateBefore,
		dueDateAfter: migratedFilters.dueDateAfter,
		dueDateBefore: migratedFilters.dueDateBefore,
		category: migratedFilters.category || [],
		status: migratedFilters.status || [],
		transparency: migratedFilters.transparency || [],
		outlook: migratedFilters.outlook || [],
		showSharedOnly: migratedFilters.showSharedOnly || [],
		unseenUpdatesOnly: migratedFilters.unseenUpdatesOnly || [],
		alignedTo: migratedFilters.alignedTo ?? undefined,
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={EmployeeGoalsFilterSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		>
			<div className="space-y-4">
				<FormField<{ id: string; name: string }[]>
					id="assignee"
					label="Assignee"
					input={(field) => (
						<MultipleSelectEmployee
							value={field.value || []}
							onChange={field.onChange}
							placeholder="Make a selection"
						/>
					)}
				/>

				<FormDatePicker id="createdDateAfter" label="Created After Date" clearable />

				<FormDatePicker id="createdDateBefore" label="Created Before Date" clearable />

				<FormDatePicker id="dueDateAfter" label="Due Date After" clearable />

				<FormDatePicker id="dueDateBefore" label="Due Date Before" clearable />

				<FormMultipleSelect<{ id: string; name: string }>
					id="category"
					label="Category"
					fetchOptions={createFetchCategoryOptions(categoriesCache)}
					selectionVariant="combobox"
					placeholder="Make a selection"
				/>

				<FormCheckboxGroup id="status" label="Status" options={statusOptions} />

				<FormCheckboxGroup id="transparency" label="Transparency Level" options={transparencyOptions} />

				<FormMultipleSelect<{ id: string; name: string; color: string }>
					id="outlook"
					label="Outlook"
					fetchOptions={createFetchOutlookOptions(outlooksData)}
					selectionVariant="combobox"
					placeholder="Select a goal outlook"
					renderOption={(option) => (
						<div className="flex items-center gap-2">
							<div className="h-3 w-3 rounded-full" style={{ backgroundColor: option.value.color }} />
							<span>{option.label}</span>
						</div>
					)}
				/>

				<FormField<string[]>
					id="showSharedOnly"
					label=""
					input={(field) => {
						const checkboxId = `showSharedOnly-checkbox`;
						const labelId = `${checkboxId}-label`;
						const isChecked = field.value?.includes("True") || false;
						return (
							<div className="flex items-start gap-2">
								<Checkbox
									id={checkboxId}
									checked={isChecked}
									onCheckedChange={(checked) => {
										field.onChange(checked ? ["True"] : []);
									}}
									aria-labelledby={labelId}
								/>
								<label
									className="cursor-pointer"
									id={labelId}
									onClick={(e) => {
										e.preventDefault();
										field.onChange(!isChecked ? ["True"] : []);
									}}
								>
									<Text size="paragraph-3">Show Shared Only</Text>
								</label>
							</div>
						);
					}}
				/>

				<FormField<string[]>
					id="unseenUpdatesOnly"
					label=""
					input={(field) => {
						const checkboxId = `unseenUpdatesOnly-checkbox`;
						const labelId = `${checkboxId}-label`;
						const isChecked = field.value?.includes("True") || false;
						return (
							<div className="flex items-start gap-2">
								<Checkbox
									id={checkboxId}
									checked={isChecked}
									onCheckedChange={(checked) => {
										field.onChange(checked ? ["True"] : []);
									}}
									aria-labelledby={labelId}
								/>
								<label
									className="cursor-pointer"
									id={labelId}
									onClick={(e) => {
										e.preventDefault();
										field.onChange(!isChecked ? ["True"] : []);
									}}
								>
									<Text size="paragraph-3">Only show goals with new updates</Text>
								</label>
							</div>
						);
					}}
				/>

				{goalDashboardFeatureEnabled && (
					<FormField<{
						id: string;
						name: string;
						status: string;
						color: string;
						assignee: string;
						created: string;
						due: string;
						target: number;
						goalValue: number;
						progress: number;
						categories: string[];
					} | null>
						id="alignedTo"
						label="Goal Alignment"
						input={(field) => (
							<SingleSelectGoalAlignment
								value={field.value}
								onChange={field.onChange}
								placeholder="Choose a goal"
							/>
						)}
					/>
				)}
			</div>
		</PanelFilters>
	);
};

export default EmployeeGoalsPanel;
